import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { startTour } from "@/lib/tours";
import { guideForRoute, faqsForRoute, TOUR_FOR_ROUTE } from "@/lib/helpContent";
import {
  Question, X, ArrowRight, Rocket, BookOpen, CaretDown, LightbulbFilament,
} from "@phosphor-icons/react";

// Slide-in, context-sensitive help for the current page: the most relevant how-to
// guide, a one-click guided tour, and the FAQs people ask on this screen.
export default function HelpDrawer({ route }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const guide = guideForRoute(route);
  const faqs = faqsForRoute(route);
  const tourId = TOUR_FOR_ROUTE[route] || "welcome";
  const anchor = guide ? `${guide.tag}-${guide.i}` : null;

  // Reset the expanded FAQ whenever the page (and therefore the content) changes.
  useEffect(() => { setOpenFaq(null); }, [route]);

  const openDrawer = () => {
    setOpen(true);
  };

  const openFullGuide = () => {
    setOpen(false);
    if (anchor) {
      api.post("/learning/progress", { kind: "guide", key: anchor }).catch(() => {});
      nav(`/help?focus=${encodeURIComponent(anchor)}`);
    } else nav("/help");
  };

  const runTour = () => {
    setOpen(false);
    setTimeout(() => startTour(tourId), 250);
  };

  return (
    <>
      {!open && (
        <button onClick={openDrawer} data-testid="help-drawer-launcher" title="Help for this page"
          className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 bg-white text-navy border border-slate-200 rounded-full pl-3 pr-4 py-2.5 text-sm font-semibold hover:border-orange transition-colors"
          style={{ boxShadow: "0 8px 24px rgba(11,26,47,0.18)" }}>
          <Question size={18} weight="bold" style={{ color: "#F37A24" }} /> Help
        </button>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-50 bg-navy/30" data-testid="help-drawer-overlay" onClick={() => setOpen(false)} />
          <aside data-testid="help-drawer"
            className="fixed top-0 right-0 z-50 h-full w-[min(420px,100vw)] bg-white flex flex-col shadow-2xl fade-up">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-navy text-white shrink-0">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                  <Question size={17} weight="bold" style={{ color: "#F37A24" }} />
                </span>
                <div>
                  <div className="font-heading font-semibold text-sm leading-none">Help for this page</div>
                  <div className="text-[0.68rem] text-white/60 mt-0.5">Guides, a tour and quick answers</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="help-drawer-close"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {/* Relevant guide */}
              {guide && (
                <div data-testid="help-drawer-guide">
                  <div className="overline text-orangeink mb-2">How to</div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center gap-2.5 mb-3">
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(46,163,253,0.1)" }}>
                        <guide.icon size={18} weight="duotone" className="text-orangeink" />
                      </span>
                      <h3 className="font-heading font-semibold text-navy leading-tight">{guide.title}</h3>
                    </div>
                    <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600 leading-relaxed">
                      {guide.steps.slice(0, 4).map((s, si) => <li key={si}>{s}</li>)}
                    </ol>
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <button onClick={openFullGuide} data-testid="help-drawer-open-guide"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3.5 py-2 rounded-lg transition-transform hover:scale-[1.03]"
                        style={{ background: "#F37A24" }}>
                        <BookOpen size={15} weight="bold" /> Full guide
                      </button>
                      {guide.to && (
                        <button onClick={() => { setOpen(false); nav(guide.to); }} data-testid="help-drawer-goto"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy border border-slate-200 px-3.5 py-2 rounded-lg hover:border-orange transition-colors">
                          {guide.cta} <ArrowRight size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Guided tour */}
              <div data-testid="help-drawer-tour">
                <div className="overline text-orangeink mb-2">Prefer to be shown?</div>
                <button onClick={runTour} data-testid="help-drawer-start-tour"
                  className="w-full group flex items-center justify-between gap-3 rounded-2xl border border-slate-200 hover:border-[#C2410C] hover:shadow-sm transition-all p-4 text-left">
                  <span className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.1)" }}>
                      <Rocket size={18} weight="duotone" style={{ color: "#F37A24" }} />
                    </span>
                    <span className="text-sm font-semibold text-navy">Take the interactive tour</span>
                  </span>
                  <ArrowRight size={16} className="text-slate-300 group-hover:text-[#C2410C] transition-colors" />
                </button>
              </div>

              {/* FAQs */}
              {faqs.length > 0 && (
                <div data-testid="help-drawer-faqs">
                  <div className="overline text-orangeink mb-2">Common questions</div>
                  <div className="space-y-2">
                    {faqs.map((f, fi) => (
                      <div key={fi} className="rounded-xl border border-slate-200 overflow-hidden">
                        <button onClick={() => setOpenFaq(openFaq === fi ? null : fi)} data-testid={`help-drawer-faq-${fi}`}
                          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium text-navy hover:bg-slate-50 transition-colors">
                          <span className="flex items-center gap-2">
                            <LightbulbFilament size={15} weight="duotone" style={{ color: "#F37A24" }} className="shrink-0" />
                            {f.q}
                          </span>
                          <CaretDown size={14} weight="bold" className={`shrink-0 text-slate-400 transition-transform ${openFaq === fi ? "rotate-180" : ""}`} />
                        </button>
                        {openFaq === fi && (
                          <div className="px-4 pb-3 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-5 py-3 shrink-0">
              <button onClick={() => { setOpen(false); nav("/help"); }} data-testid="help-drawer-browse-all"
                className="text-sm font-semibold text-navy/70 hover:text-orangeink transition-colors inline-flex items-center gap-1.5">
                <Question size={14} weight="bold" /> Browse the full Help Centre
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
